import { VNode } from 'preact';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { classNames } from '../utils/UNSAFE_classNames';
import { stringLiteralArray } from '../utils/UNSAFE_arrayUtils';

// AspectRatio uses the common padding-bottom as a percentage
// hack to get aspect-ratio to work.
// CSS now has an aspect-ratio attribute that makes this super easy.
// aspect-ratio is supported in all major browsers, most recently in Safari 15.
// https://developer.mozilla.org/en-US/docs/Web/CSS/aspect-ratio
// However, it isn't until jet 14 that we drop support for Safari 14.
// When jet 14 comes out we can replace the padding-bottom hack with aspect-ratio css property.
// Also a user could just put aspect-ratio directly on their dom, instead of wrapping
// it inside this AspectRatio component.

// NOTE: padding-bottom has to be on a dom node that is the child of a dom node that has
// width, not the same dom node. From css doc:
// The size of the padding as a percentage, relative to the width of the containing block.
// So the root dom node has the dimensions styling and the '::before' dom node
// has the padding information.
// 100%/ratio. 100/(4/3) = 75%
// e.g., 16/9 = 56.25%, 4/3 = 75%, 1/1=100%, 2/1=50%

const baseDivStyles = cssProps`${{
  position: 'relative',
  overflowX: 'hidden',
  overflowY: 'hidden',

  '&::before': {
    content: "''",
    display: 'block',
    paddingBottom: 'calc(100% / (var(--oj-c-PRIVATE-DO-NOT-USE-aspect-ratio)))'
  },
  // TODO: Revisit. there is no support for combinators, but combinators are necessary for the AspectRatio component, so it's an exception.
  /* @ts-ignore */
  '& > :first-child': {
    position: 'absolute',
    top: '0',
    bottom: '0',
    left: '0',
    right: '0',
    width: '100%',
    height: '100%'
  },
  /* @ts-ignore */
  '& > img': {
    objectFit: 'cover'
  },
  /* @ts-ignore */
  '& > video': {
    objectFit: 'cover'
  }
}}`;

// common aspect ratios
// The box’s preferred aspect ratio is the specified ratio of width / height.
// The ratio has a '/' as a separator and not a ':' because the '/' is what
// the CSS aspect-ratio uses.
// TODO: Don't use fixed ratios. Let them specify any number
export const ratios = stringLiteralArray([
  '9/16',
  '1/1',
  '6/5',
  '5/4',
  '4/3',
  '11/8',
  '1.43/1',
  '3/2',
  '14/9',
  '16/10',
  '1.6180/1',
  '5/3',
  '16/9',
  '1.85/1',
  '1.9/1',
  '2/1',
  '2.2/1',
  '64/21',
  '2.4/1',
  '2.414/1',
  '2.76/1',
  '32/9',
  '18/5',
  '4/1'
]);
type Ratio = typeof ratios[number];

const dimensions = stringLiteralArray(['maxWidth', 'minWidth', 'width']);

type Dimension = typeof dimensions[number];

// This component has some StyleInterpolationProps. Those are the width properties of the DimensionProps.
type StyleInterpolationProps = Pick<DimensionProps, Dimension>;

type AspectRatioProps = StyleInterpolationProps & {
  /**
   * The box’s preferred aspect ratio is the specified ratio of width / height. When ratio is 1/1
   * it is a square.
   */
  ratio?: Ratio;
  /**
   * The child.
   */
  children: VNode<any>;
};

// Create an array [dimensionInterpolations['maxWidth'], dimensionInterpolations['width'], dimensionInterpolations['minWidth']]
const widthDimensionInterpolation = Array.from(dimensions, (x) => dimensionInterpolations[x]);
const interpolations = mergeInterpolations<StyleInterpolationProps>(widthDimensionInterpolation);

/**
 * The AspectRatio component displays its content with a certain ratio based on the dimension
 * properties. Overflow content is hidden.
 *
 * It uses a common padding-bottom hack to do this. In future versions it will
 * be implemented using the CSS's aspect-ratio property when the browsers we need to support
 * all have it. For example, Safari 15 has it, but we won't drop Safari 14 until jet 14.
 *
 */
export function AspectRatio({ children, ratio = '1/1', ...props }: AspectRatioProps) {
  const { class: cls, ...styles } = interpolations(props);
  const stylesWithAspectRatioVar = { ...styles, '--oj-c-PRIVATE-DO-NOT-USE-aspect-ratio': ratio };
  return (
    <div class={classNames([baseDivStyles, cls])} style={stylesWithAspectRatioVar}>
      {children}
    </div>
  );
}
